import { useCallback } from "react"

import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom"

import { Drawer } from "~/components/ui-components/v2"
import EnvVars from "~/env-vars"

import { AddersStep } from "./AddersStep"
import { ArbitrageStep } from "./ArbitrageStep"
import { EquipmentStep } from "./EquipmentStep"
import { FinanceProductStep } from "./FinanceProductStep"
import { GreenbuttonStep } from "./GreenbuttonStep"
import { IncentivesStep } from "./IncentivesStep"
import {
  batterySetupGroupPath,
  batterySetupMenuPath,
  batterySetupStepPath,
  groupForStep,
  SETUP_STEP_LABELS,
  stepFromPath,
} from "./setup-steps"
import { SiteStep } from "./SiteStep"
import { SystemPricingStep } from "./SystemPricingStep"
import { UtilityForm } from "./UtilityForm"
import { VppProgramsStep } from "./VppProgramsStep"
import { WarrantyStep } from "./WarrantyStep"

export const BatterySetupDrawer = (): JSX.Element | null => {
  const { proposalId } = useParams<{ proposalId: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const step = stepFromPath(pathname)

  const onClose = useCallback(() => {
    if (!proposalId) {
      return
    }

    const group = groupForStep(step)
    navigate(
      group
        ? batterySetupGroupPath(proposalId, group.id)
        : batterySetupMenuPath(proposalId),
    )
  }, [navigate, proposalId, step])

  if (!proposalId || !step) {
    return null
  }

  if (step === `equipment`) {
    return (
      <EquipmentStep
        proposalId={proposalId}
        onClose={onClose}
        // Saving equipment persists the Finance Partner, so Loan Product
        // is the intended next step.
        onSaved={() =>
          navigate(batterySetupStepPath(proposalId, `finance-product`))
        }
      />
    )
  }

  if (step === `finance-product`) {
    return <FinanceProductStep proposalId={proposalId} onClose={onClose} />
  }

  if (step === `adders`) {
    return <AddersStep proposalId={proposalId} onClose={onClose} />
  }

  if (step === `warranty`) {
    return <WarrantyStep proposalId={proposalId} onClose={onClose} />
  }

  if (step === `system-pricing`) {
    return <SystemPricingStep proposalId={proposalId} onClose={onClose} />
  }

  if (step === `incentives`) {
    return <IncentivesStep proposalId={proposalId} onClose={onClose} />
  }

  if (step === `vpp-programs`) {
    return <VppProgramsStep proposalId={proposalId} onClose={onClose} />
  }

  if (step === `greenbutton`) {
    return <GreenbuttonStep proposalId={proposalId} onClose={onClose} />
  }

  if (step === `arbitrage`) {
    if (!EnvVars.BATTERY_SAVINGS_ENABLED) {
      return (
        <Navigate
          replace
          to={batterySetupGroupPath(proposalId, `home-and-utility-info`)}
        />
      )
    }
    return <ArbitrageStep proposalId={proposalId} onClose={onClose} />
  }

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title={SETUP_STEP_LABELS[step]}
      widthClass={step === `utility` ? `sm:max-w-2xl` : `sm:max-w-lg`}
    >
      {step === `site` ? <SiteStep proposalId={proposalId} /> : null}
      {step === `utility` ? (
        <UtilityForm key={proposalId} proposalId={proposalId} />
      ) : null}
    </Drawer>
  )
}
